import { ChangeDetectionStrategy, Component, computed, effect, input, output, signal, untracked } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../main/type.map.service";
import { S25ImageUploadModule } from "../modules/s25-image-upload/s25.image.upload.module";
import { ImageUploadModelI } from "../modules/s25-image-upload/s25.image.upload.component";
import { AttachmentService } from "../services/attachment.service";
import {
    resolveImageUrl,
    extractAltFromDataUrl,
    stripAltFromDataUrl,
    embedAltInDataUrl,
    FileStoreUtil,
    isDataUrlString,
    readBlobAsDataUrl,
} from "../util/filestore.util";
import { S25Util } from "../util/s25-util";
import { S25ButtonComponent } from "./s25.button.component";

@TypeManagerDecorator("s25-ng-filestore-image")
@Component({
    selector: "s25-ng-filestore-image",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, FormsModule, S25ImageUploadModule, S25ButtonComponent],
    template: `
        @if (readOnly()) {
            @if (resolvedSrc()) {
                <img
                    class="fs-image__preview"
                    [class.fs-image__preview--circle]="circle()"
                    [style.--fs-circle-size.px]="circle() ? (circleSizePx() ?? 80) : undefined"
                    [src]="resolvedSrc()"
                    [alt]="effectiveAlt()"
                    loading="lazy"
                />
            }
        } @else {
            <div class="fs-image">
                <s25-ng-image-upload
                    [(model)]="imageModel"
                    [hasCropper]="false"
                    [showPreview]="false"
                    [showClear]="false"
                    [showNote]="false"
                    (uploadedImageChange)="onImageUploaded($event)"
                ></s25-ng-image-upload>
                @if (previewSrc()) {
                    <div class="fs-image__current">
                        <img
                            class="fs-image__preview"
                            [class.fs-image__preview--circle]="circle()"
                            [style.--fs-circle-size.px]="circle() ? (circleSizePx() ?? 80) : undefined"
                            [src]="previewSrc()"
                            [alt]="effectiveAlt()"
                            loading="lazy"
                        />
                    </div>
                }
                <label class="fs-image__alt-label">
                    <span>Alt text</span>
                    <input
                        type="text"
                        class="fs-image__alt-input"
                        [ngModel]="altDraft()"
                        (ngModelChange)="altDraft.set($event)"
                        placeholder="Describe the image"
                    />
                </label>
                <div class="fs-image__actions">
                    @if (saveButton()) {
                        <s25-ng-button [type]="'primary'" [onClick]="save" [disabled]="!canSave()"
                            >Save Image</s25-ng-button
                        >
                    }
                    @if (value() && !hasPendingClear()) {
                        <s25-ng-button [type]="'danger--outline'" [onClick]="clear">Clear</s25-ng-button>
                    }
                </div>
                @if (errorMsg()) {
                    <p class="fs-image__error" role="alert">{{ errorMsg() }}</p>
                }
                @if (successMsg()) {
                    <p class="fs-image__success" role="status">{{ successMsg() }}</p>
                }
            </div>
        }
    `,
    styles: `
        :host {
            display: block;
        }
        .fs-image__current {
            margin-bottom: 0.5rem;
        }
        .fs-image__preview {
            max-width: 120px;
            max-height: 120px;
            border-radius: 6px;
            border: 1px solid #ddd;
            object-fit: contain;
        }
        .fs-image__preview--circle {
            width: var(--fs-circle-size, 80px);
            height: var(--fs-circle-size, 80px);
            max-width: none;
            max-height: none;
            border-radius: 50%;
            object-fit: cover;
            border: 3px solid #f0f0f0;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }
        .fs-image__alt-label {
            display: block;
            margin-top: 0.5rem;
            font-size: 0.875rem;
            font-weight: 500;
        }
        .fs-image__alt-label span {
            display: block;
            margin-bottom: 0.25rem;
        }
        .fs-image__alt-input {
            width: 100%;
            max-width: 300px;
            padding: 0.35rem 0.5rem;
            border: 1px solid #ccc;
            border-radius: 4px;
            font-size: 0.875rem;
        }
        .fs-image__actions {
            display: flex;
            gap: 0.5rem;
            margin-top: 0.65rem;
        }
        .fs-image__error {
            color: #c62828;
            font-size: 0.85rem;
            margin: 0.4rem 0 0;
        }
        .fs-image__success {
            color: #2e7d32;
            font-size: 0.85rem;
            margin: 0.4rem 0 0;
        }
    `,
})
export class S25FilestoreImageComponent {
    readonly value = input<string>("");
    readonly readOnly = input<boolean>(false);
    readonly alt = input<string>("");
    readonly objectType = input<number>(-1);
    readonly objectId = input<number>(-1);
    readonly fileKey = input<string | undefined>(undefined);
    readonly customAttributeId = input<number | undefined>(undefined);
    readonly saveButton = input<boolean>(true);
    readonly saveTrigger = input<number>(0);
    readonly autoSaveOnClear = input(false);
    /** When true, preview image is circular (e.g. avatars). */
    readonly circle = input(false);
    /** Diameter in px when {@link circle} is true; defaults to 80. */
    readonly circleSizePx = input<number | undefined>(undefined);

    readonly valueChange = output<string>();

    imageModel: ImageUploadModelI = {};
    readonly hasNewUpload = signal(false);
    readonly altDraft = signal("");
    readonly originalAlt = signal("");
    readonly errorMsg = signal("");
    readonly successMsg = signal("");

    /** Fetched data URI content from FileStore (populated by effect when value is a FileStore URL). */
    readonly fetchedData = signal<string | undefined>(undefined);

    /** FileStore coordinates parsed from value -- used for saves so we write back to the same location. */
    readonly parsedObjectType = signal<number | undefined>(undefined);
    readonly parsedObjectId = signal<number | undefined>(undefined);
    readonly parsedFileKey = signal<string | undefined>(undefined);
    readonly parsedCustomAttributeId = signal<number | undefined>(undefined);

    readonly effectiveObjectType = computed(() => this.parsedObjectType() ?? this.objectType());
    readonly effectiveObjectId = computed(() => this.parsedObjectId() ?? this.objectId());
    readonly effectiveFileKey = computed(() => this.parsedFileKey() ?? this.fileKey());
    readonly effectiveCustomAttributeId = computed(() => this.parsedCustomAttributeId() ?? this.customAttributeId());

    readonly effectiveAlt = computed(() => this.alt() || this.altDraft());
    readonly altDirty = computed(() => this.altDraft().trim() !== this.originalAlt());
    readonly canSave = computed(() => this.hasNewUpload() || this.altDirty() || this.hasPendingClear());

    readonly uploadedData = signal<string | undefined>(undefined);

    /** After Clear, hide preview until parent `[value]` clears or updates (avoids stale URL one tick). */
    private readonly previewSuppressed = signal(false);

    /** User has clicked Clear but save() has not been called yet — deletion is pending commit. */
    readonly hasPendingClear = signal(false);

    readonly resolvedSrc = computed(() => {
        if (this.previewSuppressed()) return undefined;
        const fetched = this.fetchedData();
        if (fetched && isDataUrlString(fetched)) return stripAltFromDataUrl(fetched);
        return resolveImageUrl(this.value());
    });

    readonly previewSrc = computed(() => this.uploadedData() || this.resolvedSrc());

    constructor() {
        effect(() => {
            const v = this.value();
            if (!v || !FileStoreUtil.isFileStoreUrl(v)) {
                this.fetchedData.set(undefined);
                this.parsedObjectType.set(undefined);
                this.parsedObjectId.set(undefined);
                this.parsedFileKey.set(undefined);
                this.parsedCustomAttributeId.set(undefined);
                if (!v) this.previewSuppressed.set(false);
                return;
            }
            const params = FileStoreUtil.parseFileStoreUrl(v);
            if (!params) {
                this.fetchedData.set(undefined);
                this.parsedCustomAttributeId.set(undefined);
                return;
            }
            this.parsedObjectType.set(params.objectType);
            this.parsedObjectId.set(params.objectId);
            this.parsedFileKey.set(params.fileKey);
            this.parsedCustomAttributeId.set(params.atrbId);

            AttachmentService.getFileIfExists(
                params.objectType,
                params.objectId,
                params.fileName,
                "inline",
                params.fileKey,
                params.atrbId,
            )
                .then(async (blob) => {
                    if (!blob) return;
                    //Just check the start of the file to make a guess at the type of file without the load of converting the whole blob to text
                    const head = await blob.slice(0, 32).text();
                    if (isDataUrlString(head)) {
                        const text = await blob.text();
                        this.fetchedData.set(text);
                        const embeddedAlt = extractAltFromDataUrl(text);
                        this.originalAlt.set(embeddedAlt);
                        if (!this.altDraft()) {
                            if (embeddedAlt) this.altDraft.set(embeddedAlt);
                        }
                    } else {
                        const dataUrl = await readBlobAsDataUrl(blob);
                        this.fetchedData.set(dataUrl);
                        this.originalAlt.set("");
                    }
                })
                .catch(() => {});
        });

        effect(() => {
            const trigger = this.saveTrigger();
            if (trigger > 0) {
                untracked(() => {
                    void (async () => {
                        if (this.canSave()) {
                            await this.save();
                        }
                    })();
                });
            }
        });
    }

    onImageUploaded(base64Data: string) {
        const hasFile = !!this.imageModel.getImageFileName?.();
        this.hasNewUpload.set(hasFile);
        this.uploadedData.set(hasFile ? base64Data : undefined);
        if (hasFile) this.successMsg.set("");
    }

    // only save if the component is not read-only and there is something to save
    async guardedSave(): Promise<void> {
        if (this.readOnly() || !this.canSave()) return;
        await this.save();
    }

    save = async () => {
        this.errorMsg.set("");
        this.successMsg.set("");

        if (this.hasPendingClear() && !this.hasNewUpload()) {
            const v = this.value();
            if (v && FileStoreUtil.isFileStoreUrl(v)) {
                const params = FileStoreUtil.parseFileStoreUrl(v);
                if (params?.fileName) {
                    const [, err] = await S25Util.Maybe(
                        AttachmentService.delFile(params.objectType, params.objectId, params.fileName, params.fileKey),
                        false,
                    );
                    if (err) {
                        this.errorMsg.set(S25Util.errorText(err, "Failed to delete image."));
                        return;
                    }
                }
            }
            this.resetLocalPreviewState();
            this.valueChange.emit("");
            this.successMsg.set("Image cleared.");
            return;
        }

        const newImageData = this.imageModel.getImageData ? await this.imageModel.getImageData() : undefined;
        const existingData = this.fetchedData();
        const imageData =
            newImageData ||
            (existingData && isDataUrlString(existingData) ? stripAltFromDataUrl(existingData) : undefined);
        if (!imageData) {
            this.errorMsg.set("No image selected.");
            return;
        }

        const extension = this.imageModel.getImageType?.() || "png";
        const originalName = this.imageModel.getImageFileName?.() || `image.${extension}`;
        const fileName = newImageData ? originalName : this.resolveExistingFileName() || originalName;

        const altText = this.altDraft().trim();
        const dataToStore = altText ? embedAltInDataUrl(imageData, altText) : imageData;

        const objType = this.effectiveObjectType();
        const objId = this.effectiveObjectId();
        const fk = this.effectiveFileKey();
        const caId = this.effectiveCustomAttributeId();

        try {
            await AttachmentService.delFile(objType, objId, fileName, fk);
        } catch (_) {}

        const [, error] = await S25Util.Maybe(
            AttachmentService.postFile(objType, objId, dataToStore, fileName, caId, fk),
        );

        if (error) {
            this.errorMsg.set(S25Util.errorText(error, "Failed to upload image."));
            return;
        }

        const params: Record<string, string> = {
            object_type: String(objType),
            object_id: String(objId),
            file_name: AttachmentService.sanitizeFilenameSpaces(fileName) ?? fileName,
        };
        if (fk) params.file_key = fk;
        if (caId != null) params.atrb_id = String(caId);
        const fsValue = new URLSearchParams(params).toString();

        this.fetchedData.set(dataToStore);
        this.originalAlt.set(this.altDraft().trim());
        this.valueChange.emit(fsValue);
        this.hasNewUpload.set(false);
        this.uploadedData.set(undefined);
        this.hasPendingClear.set(false);
        this.successMsg.set("Image saved.");
    };

    private resolveExistingFileName(): string | undefined {
        const v = this.value();
        if (!v) return undefined;
        const params = FileStoreUtil.parseFileStoreUrl(v);
        return params?.fileName;
    }

    clear = async () => {
        this.errorMsg.set("");
        this.hasPendingClear.set(true);
        this.previewSuppressed.set(true);
        // Clear display-only state; preserve parsed coordinates so save() can delete the right file
        this.fetchedData.set(undefined);
        this.uploadedData.set(undefined);
        this.hasNewUpload.set(false);
        this.altDraft.set("");
        this.originalAlt.set("");
        if (this.autoSaveOnClear()) {
            return this.save();
        }
    };

    private resetLocalPreviewState() {
        this.fetchedData.set(undefined);
        this.uploadedData.set(undefined);
        this.hasNewUpload.set(false);
        this.hasPendingClear.set(false);
        this.altDraft.set("");
        this.originalAlt.set("");
        this.parsedObjectType.set(undefined);
        this.parsedObjectId.set(undefined);
        this.parsedFileKey.set(undefined);
        this.parsedCustomAttributeId.set(undefined);
    }
}
